import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { financeApi } from "@/services/finance-api";
import type {
  CadastrarClienteOmieBody,
  ListarOsParams,
  OmieEmpresaChave,
  OmieCompetenciaBody,
  OmieCopiarParaCadastroBody,
  OmieTipoCadastro,
  OmieVinculoBody,
  OmieVinculoFiltro,
} from "@/types/finance";

// Keys por shape (mesma forma de parâmetros em toda a família), pra poder
// invalidar por prefixo sem colidir com um SELECT diferente na mesma chave.
const STATUS_KEY = ["finance", "omie-status"] as const;
const COMPETENCIAS_KEY = ["finance", "omie-competencias"] as const;

function clientesKey(params: {
  vinculo: OmieVinculoFiltro;
  q: string;
  page: number;
  page_size: number;
}) {
  return ["finance", "omie-clientes", params] as const;
}

function candidatosKey(params: { tipo: OmieTipoCadastro; q: string }) {
  return ["finance", "omie-candidatos", params] as const;
}

export function useOmieStatus() {
  return useQuery({
    queryKey: STATUS_KEY,
    queryFn: () => financeApi.getOmieStatus(),
  });
}

export function useOmieSincronizarClientes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => financeApi.sincronizarClientes(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: ["finance", "omie-clientes"] });
    },
  });
}

export function useOmieClientes(params: {
  vinculo: OmieVinculoFiltro;
  q: string;
  page: number;
  page_size: number;
}) {
  return useQuery({
    queryKey: clientesKey(params),
    queryFn: () => financeApi.listarClientesOmie(params),
    placeholderData: (previousData) => previousData,
  });
}

export function useOmieCandidatos(params: { tipo: OmieTipoCadastro; q: string }, enabled: boolean) {
  return useQuery({
    queryKey: candidatosKey(params),
    queryFn: () => financeApi.listarCandidatos(params),
    enabled,
  });
}

export function useOmieVincular() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clienteId, body }: { clienteId: string; body: OmieVinculoBody }) =>
      financeApi.vincularCliente(clienteId, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: ["finance", "omie-clientes"] });
    },
  });
}

export function useOmieDesvincular() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clienteId: string) => financeApi.desvincularCliente(clienteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: STATUS_KEY });
      queryClient.invalidateQueries({ queryKey: ["finance", "omie-clientes"] });
    },
  });
}

export function useOmieConferirVinculo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clienteId: string) => financeApi.conferirVinculo(clienteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finance", "omie-clientes"] });
    },
  });
}

export function useOmieCopiarParaCadastro() {
  return useMutation({
    mutationFn: ({ clienteId, body }: { clienteId: string; body: OmieCopiarParaCadastroBody }) =>
      financeApi.copiarParaCadastro(clienteId, body),
  });
}

export function useOmieCompetencias() {
  return useQuery({
    queryKey: COMPETENCIAS_KEY,
    queryFn: () => financeApi.getCompetencias(),
  });
}

export function useOmieAtualizarCompetencia() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      competencia,
      body,
    }: {
      competencia: string;
      body: OmieCompetenciaBody;
    }) => financeApi.atualizarCompetencia(competencia, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COMPETENCIAS_KEY });
    },
  });
}

export function useSincronizarOs() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => financeApi.sincronizarOs(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["finance", "os-historico"] });
      queryClient.invalidateQueries({ queryKey: ["finance", "proposta-ordens-servico"] });
    },
  });
}

export function useOsHistorico(params: ListarOsParams) {
  return useQuery({
    queryKey: ["finance", "os-historico", params],
    queryFn: () => financeApi.listarOsHistorico(params),
    placeholderData: (anterior) => anterior,
  });
}

// O cadastro e o documento mudam o que a OS da proposta enxerga ("sem vínculo", o CNPJ):
// invalida a prévia das OS e os documentos junto com a lista de clientes.
function invalidarDepoisDoCadastro(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: STATUS_KEY });
  queryClient.invalidateQueries({ queryKey: ["finance", "omie-clientes"] });
  queryClient.invalidateQueries({ queryKey: ["finance", "proposta-ordens-servico"] });
  queryClient.invalidateQueries({ queryKey: ["finance", "proposta"] });
  queryClient.invalidateQueries({ queryKey: ["finance", "proposta-preview"] });
}

export function useRascunhoClienteOmie(
  params: { tipo: OmieTipoCadastro; id: string; empresa: OmieEmpresaChave },
  enabled: boolean,
) {
  return useQuery({
    queryKey: ["finance", "omie-cadastro", params] as const,
    queryFn: () => financeApi.rascunhoClienteOmie(params.tipo, params.id, params.empresa),
    enabled,
    staleTime: 0,
  });
}

export function useCadastrarClienteOmie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ tipo, id, body }: { tipo: OmieTipoCadastro; id: string; body: CadastrarClienteOmieBody }) =>
      financeApi.cadastrarClienteOmie(tipo, id, body),
    onSuccess: () => invalidarDepoisDoCadastro(queryClient),
  });
}

export function useVincularDaProposta() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ clienteId, body }: { clienteId: string; body: OmieVinculoBody }) =>
      financeApi.vincularCliente(clienteId, body),
    onSuccess: () => invalidarDepoisDoCadastro(queryClient),
  });
}

export function useEnviarDocumentoAoOmie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clienteId: string) => financeApi.enviarDocumentoAoOmie(clienteId),
    onSuccess: () => invalidarDepoisDoCadastro(queryClient),
  });
}

export function useTrazerDocumentoDoOmie() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (clienteId: string) => financeApi.trazerDocumentoDoOmie(clienteId),
    onSuccess: () => invalidarDepoisDoCadastro(queryClient),
  });
}
